import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Checkbox, Icon, Input, Select, Textarea } from "@legal-500/legal500-design-system";
import { industrySectors } from "../../api/industrySectors.service";
import {
  POWERLIST_IMAGE_UPLOAD_ENDPOINT,
  powerlistBadges,
  powerlistEditions,
  powerlistEntries,
  powerlistLists,
  type Badge,
  type Entry,
  type EntryCompany,
  type EntryFields,
  type ImageRef,
} from "../../api/powerlist.service";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../components/LoadingState";
import { PageHeader } from "../../components/PageHeader";
import { ImageUploadField } from "../../components/Profiles/ImageUploadField";
import { StatusBadge } from "../../components/StatusBadge";
import { useQuery } from "../../hooks/useApi";
import { ROUTES } from "../../routes";
import { EntryAnswersSection } from "./EntryAnswersSection";
import { EntryCompanyPicker } from "./EntryCompanyPicker";
import {
  ConfirmModal,
  FormError,
  errorMessage,
  textOrNull,
  usePowerlistAccess,
  useToast,
} from "./shared";
import styles from "./Powerlist.module.css";

type FormValues = {
  listId: string;
  name: string;
  surname: string;
  slug: string;
  jobTitle: string;
  companyMode: "picker" | "text";
  companyId: string | null;
  companyName: string;
  companyQuery: string;
  industrySectorId: string;
  biography: string;
  teamSize: string;
  majorLegalAdvisors: string;
  jurisdictionsCovered: string;
  photo: ImageRef | null;
  logo: ImageRef | null;
};

function formValuesOf(entry: Entry | null, defaultListId: string): FormValues {
  return {
    listId: entry?.listId ?? defaultListId,
    name: entry?.name ?? "",
    surname: entry?.surname ?? "",
    slug: entry?.slug ?? "",
    jobTitle: entry?.jobTitle ?? "",
    companyMode: entry?.companyId || !entry?.companyName ? "picker" : "text",
    companyId: entry?.companyId ?? null,
    companyName: entry?.companyName ?? "",
    companyQuery: entry?.companyId ? (entry.companyName ?? "") : "",
    industrySectorId: entry?.industrySectorId ?? "",
    biography: entry?.biography ?? "",
    teamSize: entry?.teamSize ?? "",
    majorLegalAdvisors: entry?.majorLegalAdvisors ?? "",
    jurisdictionsCovered: entry?.jurisdictionsCovered ?? "",
    photo: entry?.photo ?? null,
    logo: entry?.logo ?? null,
  };
}

const COMPANY_NOT_PICKED = "Pick a company from the list, clear it, or type the name instead";

function companyQueryMatchesPick(values: FormValues): boolean {
  const query = values.companyQuery.trim();
  return query === "" || (values.companyId !== null && query === values.companyName.trim());
}

function entryFieldsOf(values: FormValues): EntryFields {
  const pickerCleared = values.companyMode === "picker" && values.companyQuery.trim() === "";
  return {
    name: values.name.trim(),
    surname: textOrNull(values.surname),
    jobTitle: textOrNull(values.jobTitle),
    companyId: values.companyMode === "picker" && !pickerCleared ? values.companyId : null,
    companyName: pickerCleared ? null : textOrNull(values.companyName),
    photo: values.photo,
    logo: values.logo,
    industrySectorId: values.industrySectorId || null,
    biography: textOrNull(values.biography),
    teamSize: textOrNull(values.teamSize),
    majorLegalAdvisors: textOrNull(values.majorLegalAdvisors),
    jurisdictionsCovered: textOrNull(values.jurisdictionsCovered),
  };
}

type BadgesSectionProps = {
  entry: Entry;
  badges: Badge[];
  onChanged: (message: string) => Promise<void>;
  onError: (error: unknown) => void;
};

function BadgesSection({ entry, badges, onChanged, onError }: BadgesSectionProps) {
  const { canWrite, canDelete } = usePowerlistAccess();
  const held = new Set(entry.badges.map(badge => badge.id));
  const [busy, setBusy] = useState(false);

  async function toggle(badge: Badge, on: boolean) {
    setBusy(true);
    try {
      if (on) await powerlistBadges.add(entry.id, badge.id);
      else await powerlistBadges.remove(entry.id, badge.id);
      await onChanged(on ? `${badge.name} badge added` : `${badge.name} badge removed`);
    } catch (toggleError) {
      onError(toggleError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.section} aria-labelledby="powerlist-badges-heading">
      <h2 id="powerlist-badges-heading" className={styles.sectionTitle}>
        Badges
      </h2>
      <div className={styles.fieldset}>
        {badges.map(badge => {
          const checked = held.has(badge.id);
          return (
            <Checkbox
              key={badge.id}
              label={badge.name}
              aria-label={badge.name}
              checked={checked}
              disabled={busy || (checked ? !canDelete : !canWrite)}
              onChange={event => void toggle(badge, event.target.checked)}
            />
          );
        })}
      </div>
    </section>
  );
}

export default function EntryEditorPage() {
  const { editionId = "", entryId } = useParams();
  const isNew = entryId === undefined;
  const navigate = useNavigate();
  const { canWrite, canPublish, canDelete } = usePowerlistAccess();
  const { toast, success, failure } = useToast();

  const { data, loading, error, refetch, isRefetching } = useQuery(async () => {
    const [edition, lists, sectors, badges, entry] = await Promise.all([
      powerlistEditions.get(editionId),
      powerlistLists.list(editionId),
      industrySectors.getAll({ isActive: true }),
      powerlistBadges.list(),
      entryId ? powerlistEntries.get(entryId) : Promise.resolve(null),
    ]);
    return {
      edition,
      lists: [...lists].sort((a, b) => a.sortOrder - b.sortOrder),
      sectors: [...sectors].sort((a, b) => a.name.localeCompare(b.name)),
      badges,
      entry,
    };
  }, [editionId, entryId]);

  const [values, setValues] = useState<FormValues>(() => formValuesOf(null, ""));
  const [formError, setFormError] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploads, setUploads] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const [seededFor, setSeededFor] = useState<string | null>(null);
  const seedKey = data ? `${data.edition.id}:${data.entry?.id ?? "new"}` : null;
  if (data && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setCompanyError(null);
    setValues(formValuesOf(data.entry, data.lists[0]?.id ?? ""));
  }

  function set<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues(previous => ({ ...previous, [key]: value }));
  }

  function pickCompany(company: EntryCompany | null) {
    setCompanyError(null);
    setValues(previous => ({
      ...previous,
      companyId: company?.id ?? null,
      companyName: company?.name ?? "",
      companyQuery: company?.name ?? "",
    }));
  }

  function setCompanyQuery(companyQuery: string) {
    setCompanyError(null);
    setValues(previous => ({ ...previous, companyQuery }));
  }

  function setCompanyMode(companyMode: FormValues["companyMode"]) {
    setCompanyError(null);
    setValues(previous => ({
      ...previous,
      companyMode,
      companyId: null,
      companyName: companyMode === "text" ? previous.companyName : "",
      companyQuery: "",
    }));
  }

  function trackUpload(uploading: boolean) {
    setUploads(count => Math.max(0, count + (uploading ? 1 : -1)));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!values.name.trim()) {
      setFormError("Name is required");
      return;
    }
    if (!values.listId) {
      setFormError("Choose a list");
      return;
    }
    if (values.companyMode === "picker" && !companyQueryMatchesPick(values)) {
      setCompanyError(COMPANY_NOT_PICKED);
      return;
    }
    setSaving(true);
    setFormError(null);
    const fields = entryFieldsOf(values);
    try {
      if (!data?.entry) {
        const created = await powerlistEntries.create(values.listId, {
          ...fields,
          slug: values.slug.trim() || undefined,
        });
        success(`${created.name} created as a draft`);
        navigate(ROUTES.powerlist.entry(editionId, created.id), { replace: true });
      } else {
        const saved = await powerlistEntries.update(data.entry.id, {
          ...fields,
          listId: values.listId,
          slug:
            values.slug.trim() && values.slug.trim() !== data.entry.slug
              ? values.slug.trim()
              : undefined,
        });
        setValues(formValuesOf(saved, saved.listId));
        success(`${fields.name} saved`);
        await refetch();
      }
    } catch (saveError) {
      setFormError(errorMessage(saveError));
    } finally {
      setSaving(false);
    }
  }

  async function setStatus(status: Entry["status"]) {
    if (!data?.entry) return;
    try {
      await powerlistEntries.update(data.entry.id, { status });
      success(status === "published" ? "Entry published" : "Entry moved back to draft");
      await refetch();
    } catch (statusError) {
      failure(statusError);
    }
  }

  const entriesUrl = `${ROUTES.powerlist.edition(editionId)}?tab=entries`;

  if (loading && data === null) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title={isNew ? "New entry" : "Entry"} />
        <LoadingState message="Loading entry..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title={isNew ? "New entry" : "Entry"} />
        <ErrorBanner
          error={error?.message ?? "Entry not found"}
          onRetry={refetch}
          loading={isRefetching}
        />
      </div>
    );
  }

  const { entry, edition, lists, sectors, badges } = data;
  const sectorIsInactive =
    values.industrySectorId !== "" &&
    !sectors.some(sector => sector.id === values.industrySectorId);
  const readOnly = !canWrite;
  const selectedCompany: EntryCompany | null = values.companyId
    ? { id: values.companyId, name: values.companyName }
    : null;

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={entry ? entry.name : "New entry"}
        subtitle={edition.title}
        meta={entry ? <StatusBadge status={entry.status} /> : undefined}
      >
        <div className={styles.headerActions}>
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={() => navigate(entriesUrl)}
          >
            <Icon icon="arrow-left" size="small" />
            All entries
          </Button>
          {entry && canPublish && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => void setStatus(entry.status === "draft" ? "published" : "draft")}
            >
              {entry.status === "draft" ? "Publish entry" : "Move entry to draft"}
            </Button>
          )}
          {entry && canDelete && (
            <Button variant="outlined" color="error" size="small" onClick={() => setDeleting(true)}>
              Delete entry
            </Button>
          )}
        </div>
      </PageHeader>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      <form
        className={styles.section}
        onSubmit={handleSubmit}
        noValidate
        aria-label="Entry details"
      >
        <h2 className={styles.sectionTitle}>Details</h2>
        <FormError message={formError} />
        <div className={styles.formRow}>
          <Select
            id="powerlist-entry-list"
            label="List"
            value={values.listId}
            onChange={event => set("listId", event.target.value)}
            options={lists.map(list => ({ value: list.id, label: list.title }))}
            disabled={readOnly}
            fullWidth
          />
          <Input
            id="powerlist-entry-name"
            label="Name"
            value={values.name}
            onChange={event => set("name", event.target.value)}
            required
            disabled={readOnly}
            fullWidth
          />
          <Input
            id="powerlist-entry-surname"
            label="Surname"
            value={values.surname}
            onChange={event => set("surname", event.target.value)}
            helperText="Optional. Used to sort the list alphabetically"
            disabled={readOnly}
            fullWidth
          />
          <Input
            id="powerlist-entry-job-title"
            label="Job title"
            value={values.jobTitle}
            onChange={event => set("jobTitle", event.target.value)}
            disabled={readOnly}
            fullWidth
          />
          <div className={styles.companyField}>
            {values.companyMode === "picker" ? (
              <>
                <EntryCompanyPicker
                  value={selectedCompany}
                  query={values.companyQuery}
                  onQueryChange={setCompanyQuery}
                  onChange={pickCompany}
                  error={companyError ?? undefined}
                  disabled={readOnly}
                />
                <p className={styles.muted}>Searches the Website users companies list.</p>
                {!readOnly && (
                  <div className={styles.actions}>
                    {(values.companyId || values.companyQuery) && (
                      <Button variant="text" size="small" onClick={() => pickCompany(null)}>
                        Clear company
                      </Button>
                    )}
                    <Button variant="text" size="small" onClick={() => setCompanyMode("text")}>
                      Company not in the list? Type the name instead
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <Input
                  id="powerlist-entry-company"
                  label="Company name"
                  value={values.companyName}
                  onChange={event => set("companyName", event.target.value)}
                  disabled={readOnly}
                  fullWidth
                />
                {!readOnly && (
                  <Button variant="text" size="small" onClick={() => setCompanyMode("picker")}>
                    Pick from the companies list instead
                  </Button>
                )}
              </>
            )}
          </div>
          <Select
            id="powerlist-entry-sector"
            label="Industry sector"
            value={values.industrySectorId}
            onChange={event => set("industrySectorId", event.target.value)}
            options={[
              { value: "", label: "No sector" },
              ...(sectorIsInactive
                ? [{ value: values.industrySectorId, label: "Current sector (no longer active)" }]
                : []),
              ...sectors.map(sector => ({ value: sector.id, label: sector.name })),
            ]}
            disabled={readOnly}
            fullWidth
          />
          <Input
            id="powerlist-entry-slug"
            label="Slug"
            value={values.slug}
            onChange={event => set("slug", event.target.value)}
            helperText={entry ? undefined : "Leave blank to generate it from the name"}
            disabled={readOnly}
            fullWidth
          />
        </div>
        <div className={styles.formRow}>
          <div className={styles.imageField}>
            <ImageUploadField
              label="Photo"
              value={values.photo}
              onChange={photo => set("photo", photo)}
              slug={values.name}
              uploadEndpoint={POWERLIST_IMAGE_UPLOAD_ENDPOINT}
              uploadLabel="Upload photo"
              changeLabel="Change photo"
              removeLabel="Remove photo"
              disabled={readOnly}
              onUploadingChange={trackUpload}
            />
            <p className={styles.muted}>
              Square headshot, at least 400 × 400 px (JPG or PNG). It's shown in a circle, so keep
              the face centred.
            </p>
          </div>
          <div className={styles.imageField}>
            <ImageUploadField
              label="Company logo"
              value={values.logo}
              onChange={logo => set("logo", logo)}
              slug={values.companyName || values.name}
              uploadEndpoint={POWERLIST_IMAGE_UPLOAD_ENDPOINT}
              uploadLabel="Upload company logo"
              changeLabel="Change company logo"
              removeLabel="Remove company logo"
              disabled={readOnly}
              onUploadingChange={trackUpload}
            />
            <p className={styles.muted}>
              Square logo, at least 400 × 400 px, on a white or transparent background (PNG).
            </p>
          </div>
        </div>
        <Textarea
          id="powerlist-entry-biography"
          label="Biography"
          value={values.biography}
          onChange={event => set("biography", event.target.value)}
          rows={6}
          disabled={readOnly}
          fullWidth
        />
        <Input
          id="powerlist-entry-team-size"
          label="Team size"
          value={values.teamSize}
          onChange={event => set("teamSize", event.target.value)}
          helperText="Optional"
          disabled={readOnly}
          fullWidth
        />
        <Textarea
          id="powerlist-entry-advisors"
          label="Major legal advisors"
          value={values.majorLegalAdvisors}
          onChange={event => set("majorLegalAdvisors", event.target.value)}
          rows={3}
          disabled={readOnly}
          fullWidth
        />
        <Textarea
          id="powerlist-entry-jurisdictions"
          label="Jurisdictions covered"
          value={values.jurisdictionsCovered}
          onChange={event => set("jurisdictionsCovered", event.target.value)}
          rows={3}
          disabled={readOnly}
          fullWidth
        />
        {canWrite && (
          <div className={styles.formActions}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || uploads > 0}
            >
              {saving ? "Saving..." : entry ? "Save entry" : "Create entry"}
            </Button>
          </div>
        )}
      </form>

      {entry ? (
        <>
          <BadgesSection
            entry={entry}
            badges={badges}
            onChanged={async message => {
              success(message);
              await refetch();
            }}
            onError={failure}
          />
          <EntryAnswersSection editionId={editionId} entryId={entry.id} onMessage={success} />
        </>
      ) : (
        <p className={styles.muted}>
          Badges and interview answers can be added once the entry is created.
        </p>
      )}

      {entry && (
        <ConfirmModal
          isOpen={deleting}
          title="Delete entry"
          message={`Delete ${entry.name} with its badges and answers?`}
          confirmLabel="Delete entry"
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await powerlistEntries.remove(entry.id);
            navigate(entriesUrl);
          }}
        />
      )}
      {toast}
    </div>
  );
}
